import { fetchAuthSession } from '@aws-amplify/auth'

import api from '../../modules/services/api'
import store from '../../store'
import { showToasts } from '../toasts'
import { ExtendedError, getNonFieldErrorMessage, parseError } from '../errors'
import { captureErrorToSentry } from './requestErrorsCapturing'

import { isAuthorizedSelector } from '../../modules/selectors/cognito'
import { submitLogout } from '../../modules/actions/cognito'

import { REACT_APP_ENVIRONMENTS, TOAST_TYPE } from '../../constants/other'
import { ENDPOINTS } from '../../constants/api'

export function isAwsRequest(url) {
  return !!url.includes('s3.amazonaws.com')
}

export default async function getToken() {
  // getting token from AWS provides having correct token everytime
  // session tokens expires every 1hr, and fetchAuthSession() manage to get a new one automatically
  return await fetchAuthSession()
    .then(session => session.tokens?.accessToken?.toString())
    .catch(sessionError => {
      console.error('current session:', sessionError)
      return ''
    })
}

export function checkAuthToken(token, apiUrl) {
  const currentState = store.getState()
  const isUserAuthorized = isAuthorizedSelector(currentState)

  if (isUserAuthorized && !token) {
    // if user is authorized but token is not available, then we need to logout the user
    // so that user can login again, this is a case when token has expired
    console.warn('Failed api point:', apiUrl)
    store.dispatch(submitLogout())

    throw new ExtendedError('Your session has expired. Please log in again.', {
      showErrorToast: false
    })
  }
}

export async function madeRequest(
  method,
  { data = {}, params = {}, skipContextPartnershipId = false, showErrorToast = true, ...otherProps }
) {
  // todo remove is_sandbox for test ad account when it's not needed
  const hasTiktokSandboxAdAccount =
    params.account === '7109089981687218177' ||
    (data && (data.account === '7109089981687218177' || data.advertiser_id === '7109089981687218177')) ||
    !!data?.providers?.find(({ ad_account_id }) => ad_account_id === '7109089981687218177')
  if (hasTiktokSandboxAdAccount) {
    params.is_sandbox = true
  }

  const { partnerships } = store.getState()
  const { selectedPartnershipId } = partnerships

  // add parameter context_partnership_id with selectedPartnershipId on FE to all requests to our BE,
  // don't add it to any third party services requests
  if (
    !isAwsRequest(otherProps.url) &&
    // don't pass context_partnership_id debug tokens request
    !otherProps.url.includes(ENDPOINTS.facebookDebugTokens) &&
    !skipContextPartnershipId
  ) {
    params.context_partnership_id = selectedPartnershipId
  }

  try {
    const response = await api({
      method,
      data,
      params,
      // 90sec as BE requests timeout to 3rd party
      timeout: 90000, // should be set before ...otherProps to be able to override it from service
      timeoutErrorMessage: 'There has been a timeout, please try again',
      ...otherProps
    })

    return response.data
  } catch (error) {
    handleRequestError(error, { data, method, otherProps, params, showErrorToast })
  }
}

const handleRequestError = (error, requestData) => {
  const { showErrorToast } = requestData
  console.error(error)

  if (error.response) {
    const { data } = error.response // represent general error for forms or etc. in toast message
    // there are 2 types of errors(detail and form errors)
    // form errors are parsed and represents on form fields level, but for non_field_errors it represents in toast
    if (data) {
      const nonFieldErrorMessage = getNonFieldErrorMessage(data.non_field_errors)
      const errorMessage = data.detail || nonFieldErrorMessage
      errorMessage &&
        showErrorToast &&
        showToasts({
          type: TOAST_TYPE.error,
          message: errorMessage
        })

      handleSentryError(error, requestData, { showToast: showErrorToast, toastMessage: errorMessage })
    } else {
      handleSentryError(error, requestData)
    }

    throw parseError(error.response)
  } else {
    // hardcoded error once response is missed.
    // One of the case is the FE request timeout in this case the response is missed
    const hardcodedError = {
      message: error.message,
      code: error.code
    }
    showToasts({
      type: TOAST_TYPE.error,
      message: error.message
    })

    handleSentryError(error, requestData, { showToast: true, toastMessage: error.message })

    throw hardcodedError
  }
}

const handleSentryError = (error, requestData, toast) => {
  const { method, data, otherProps, params } = requestData
  const status = error.response?.status

  if (status === 404 || status === 429) {
    let errorMessages = ''
    if (status === 429) {
      // 429 status usually represents when user got blocked by a big amount of requests
      // track when user gets throttled because of too many requests
      errorMessages = 'status - 429: got blocked by breaking the limit of requests'
    } else if (status === 404) {
      errorMessages = 'status - 404: item not found'
    }

    captureErrorToSentry({
      message: errorMessages,
      method,
      otherProps,
      data,
      params,
      error,
      toast
    })
  } else if (process.env.REACT_APP_ENVIRONMENT === REACT_APP_ENVIRONMENTS.prod) {
    // track all requests errors happened on production
    captureErrorToSentry({
      status,
      message: `status - ${status}`,
      method,
      otherProps,
      data,
      params,
      error,
      toast
    })
  }
}

export const getFormData = object =>
  Object.keys(object).reduce((formData, key) => {
    const fieldValue = object[key]
    if (Array.isArray(fieldValue)) {
      // when there is an array in the data object it should be passed as a new separate field in FormData
      // FormData accepting multiple fields with the same names
      // i.e. {image: [file,file]} fill be transformed to: "image: file,  image: file" in FormData
      fieldValue.forEach(value => {
        formData.append(key, value)
      })
    } else {
      formData.append(key, fieldValue)
    }
    return formData
  }, new FormData())
